import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Autocomplete, Button, Icon, TextField } from '@equinor/eds-core-react'
import { DeviceDto, PlantDto, ProjectDto } from '@/core/models/types'
import { baseFont, InputStyledLabel, StyledErrorTypography, StyledHelpButton, StyledHelpIcon } from '@/styles/layout'
import { camera, error_outlined, help_outline } from '@equinor/eds-icons'
import { useAtom } from 'jotai'
import { errorFormVendorSerialNumberAtom, scannedDataAtom, showErrorSelectionBarAtom } from '@/core/store/globalStore'
import Scanner from '../Scanner'
import PopoverDialog from '@/components/popover/PopoverDialog'
import { missingVendorSerialNumber } from './OnboardingStepper'

interface OnboardSelectionBarProps {
    onClear: (type: 'plant' | 'device' | 'project') => void
    plants: PlantDto[]
    devices: DeviceDto[]
    projects: ProjectDto[]
    onSelectionChange: (type: 'plant' | 'device' | 'project', changes: any) => void
    selectedPlant: PlantDto | null
    selectedDevice: DeviceDto | null
    selectedProject: ProjectDto | null
    vendorSerialNumber: string
    onVendorSerialNumberChange: (value: string) => void
    onVendorSerialNumberCommit: (value: string) => void
    onScan: (data: any) => void
    setScannerFetchingData: (value: boolean | ((prevState: boolean) => boolean)) => void
}

export const missingPlant = 'Please select a plant.'
export const missingProject = 'Please select a project.'
export const missingDevice = "Please select a device. If you're unsure, check the portfolio for images and details."

const tooltipVendorSerialNumber =
    'You can find the vendor serial number on the sensor. To quickly add it you can scan the QR code on the sensor or type it in the input field.'

const OnboardSelectionBar: React.FC<OnboardSelectionBarProps> = ({
    onClear,
    plants,
    devices,
    projects,
    onSelectionChange,
    selectedPlant,
    selectedDevice,
    selectedProject,
    vendorSerialNumber,
    onVendorSerialNumberChange,
    onVendorSerialNumberCommit,
    onScan,
    setScannerFetchingData,
}) => {
    const [showErrorSelectionBar, setShowErrorSelectionBar] = useAtom(showErrorSelectionBarAtom)
    const [errorFormVendorSerialNumber, setErrorFormVendorSerialNumber] = useAtom(errorFormVendorSerialNumberAtom)
    const [scannedData] = useAtom(scannedDataAtom)
    const [isTooltipVendorSerialNumberOpen, setTooltipVendorSerialNumberIsOpen] = useState(false)
    const [windowWidth, setWindowWidth] = useState(window.innerWidth)
    const vendorRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth)
        window.addEventListener('resize', handleResize)
        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [])

    const applyVendorSerialNumber = (value: string) => {
        setShowErrorSelectionBar(false)
        setErrorFormVendorSerialNumber(null)
        onVendorSerialNumberChange(value)
    }

    useEffect(() => {
        if (scannedData?.serialNumber) {
            applyVendorSerialNumber(scannedData.serialNumber)
            onVendorSerialNumberCommit(scannedData.serialNumber)
        }
    }, [scannedData])

    const scannerButton = (
        <ScannerStyledButton color="primary">
            <StyledScannerIcon data={camera} size={24} />
            Scan QR
        </ScannerStyledButton>
    )

    return (
        <StyledContainer>
            <StyledAutocomplete
                label={
                    <RowStyle>
                        <InputStyledLabel>Plant</InputStyledLabel>
                    </RowStyle>
                }
                meta={
                    <RowStyle>
                        <InputStyledLabel>Mandatory</InputStyledLabel>
                    </RowStyle>
                }
                options={plants}
                optionLabel={(plant: unknown) => (plant as PlantDto).description ?? ''}
                selectedOptions={selectedPlant ? [selectedPlant] : []}
                onOptionsChange={(changes) =>
                    onSelectionChange('plant', {
                        selectedItems: changes.selectedItems.length > 0 ? changes.selectedItems : [],
                    })
                }
                variant={showErrorSelectionBar && !selectedPlant ? 'error' : undefined}
                onClear={() => onClear('plant')}
                $isError={true}
            />
            {showErrorSelectionBar && !selectedPlant && (
                <RowStyle style={{ alignItems: 'flex-start' }}>
                    {' '}
                    <StyledErrorIcon data={error_outlined} size={16} />
                    <StyledErrorTypography> {missingPlant}</StyledErrorTypography>
                </RowStyle>
            )}

            <StyledAutocomplete
                label={
                    <RowStyle>
                        <InputStyledLabel>Project</InputStyledLabel>
                    </RowStyle>
                }
                meta={
                    <RowStyle>
                        <InputStyledLabel>Mandatory</InputStyledLabel>
                    </RowStyle>
                }
                options={projects}
                optionLabel={(project: unknown) => {
                    const { projectCode, description } = project as ProjectDto
                    return projectCode && description ? `${projectCode} - ${description}` : ''
                }}
                selectedOptions={selectedProject ? [selectedProject] : []}
                onOptionsChange={(changes) =>
                    onSelectionChange('project', {
                        selectedItems: changes.selectedItems.length > 0 ? changes.selectedItems : [],
                    })
                }
                variant={showErrorSelectionBar && !selectedProject ? 'error' : undefined}
                onClear={() => onClear('project')}
                $isError={true}
                autoWidth={true}
                multiline={true}
            />
            {showErrorSelectionBar && !selectedProject && (
                <RowStyle style={{ alignItems: 'flex-start' }}>
                    {' '}
                    <StyledErrorIcon data={error_outlined} size={16} />
                    <StyledErrorTypography> {missingProject}</StyledErrorTypography>
                </RowStyle>
            )}

            <VendorRow>
                <StyledTextField
                    id="VendorSerialNumber"
                    label={
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <InputStyledLabel>
                                {windowWidth > 1024 ? 'Vendor Serial Number' : 'Serial Number'}
                            </InputStyledLabel>
                            <StyledHelpButton
                                ref={vendorRef}
                                variant="ghost_icon"
                                onClick={() => setTooltipVendorSerialNumberIsOpen(!isTooltipVendorSerialNumberOpen)}
                                $isActive={isTooltipVendorSerialNumberOpen}
                            >
                                <StyledHelpIcon
                                    data={help_outline}
                                    size={24}
                                    $isActive={isTooltipVendorSerialNumberOpen}
                                />
                            </StyledHelpButton>
                        </div>
                    }
                    value={vendorSerialNumber}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => applyVendorSerialNumber(e.target.value)}
                    onBlur={() => onVendorSerialNumberCommit(vendorSerialNumber)}
                    required
                    variant={showErrorSelectionBar && !vendorSerialNumber ? 'error' : undefined}
                    meta={
                        <RowStyle>
                            <InputStyledLabel>Mandatory</InputStyledLabel>
                        </RowStyle>
                    }
                    $isError={true}
                />
                <Scanner
                    onScan={onScan}
                    onFetchingData={setScannerFetchingData}
                    scannerButton={scannerButton}
                    isReadSensor={false}
                />
            </VendorRow>
            {showErrorSelectionBar && !vendorSerialNumber && (
                <RowStyle style={{ alignItems: 'flex-start' }}>
                    <StyledErrorIcon data={error_outlined} size={16} />
                    <StyledErrorTypography>{missingVendorSerialNumber}</StyledErrorTypography>
                </RowStyle>
            )}
            {errorFormVendorSerialNumber && (
                <RowStyle style={{ alignItems: 'flex-start' }}>
                    <StyledErrorIcon data={error_outlined} size={16} />
                    <StyledErrorTypography variant="body_short">{errorFormVendorSerialNumber}</StyledErrorTypography>
                </RowStyle>
            )}

            <StyledAutocomplete
                label={
                    <RowStyle>
                        <InputStyledLabel>Device</InputStyledLabel>
                    </RowStyle>
                }
                meta={
                    <RowStyle>
                        <InputStyledLabel>Mandatory</InputStyledLabel>
                    </RowStyle>
                }
                options={devices}
                optionLabel={(device: unknown) => (device as DeviceDto).displayName ?? ''}
                selectedOptions={selectedDevice ? [selectedDevice] : []}
                onOptionsChange={(changes) =>
                    onSelectionChange('device', {
                        selectedItems: changes.selectedItems.length > 0 ? changes.selectedItems : [],
                    })
                }
                variant={showErrorSelectionBar && !selectedDevice ? 'error' : undefined}
                onClear={() => onClear('device')}
                $isError={true}
            />
            {showErrorSelectionBar && !selectedDevice && (
                <RowStyle style={{ alignItems: 'flex-start' }}>
                    {' '}
                    <StyledErrorIcon data={error_outlined} size={16} />
                    <StyledErrorTypography> {missingDevice}</StyledErrorTypography>
                </RowStyle>
            )}
            <PopoverDialog
                open={isTooltipVendorSerialNumberOpen}
                title={'Vendor serial number'}
                content={tooltipVendorSerialNumber}
                onClose={() => setTooltipVendorSerialNumberIsOpen(false)}
                anchorEl={vendorRef.current}
            />
        </StyledContainer>
    )
}

const StyledAutocomplete = styled(Autocomplete)<{ $isError?: boolean }>`
    ${baseFont}
    @media (max-width: 1024px) {
        --eds_interactive_danger__resting: #c6002d;
        --eds_interactive_danger__hover: #c6002d;
        input {
            background-color: #f5f5f5;
            color: #1d1d1d;
        }
        .leTuEP {
            color: #a50827;
        }
        .iqpZOT {
            color: #a50827;
        }
    }
`

const StyledTextField = styled(TextField)<{ $isError?: boolean }>`
    ${baseFont}
    margin-bottom: 0px;
    @media (max-width: 1024px) {
        --eds_interactive_danger__resting: #c6002d;
        --eds_interactive_danger__hover: #c6002d;
        input {
            background-color: #f5f5f5;
            color: #1d1d1d;
        }
        .leTuEP {
            color: ${(props) => (props.$isError ? '#a50827' : '#8a4100')};
        }
        .kwyFQC {
            color: ${(props) => (props.$isError ? '#a50827' : '#8a4100')};
        }
    }
`

const ScannerStyledButton = styled(Button)`
    gap: 8px;
    padding: 6px;
    font-size: 14px;
    white-space: nowrap;
`

const StyledScannerIcon = styled(Icon)`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 22px;
    width: 24px;
`

const RowStyle = styled.div`
    display: flex;
    align-items: center;
    flex-direction: row;
    flex-wrap: wrap;
    margin-bottom: 4px;
    width: 100%;
    gap: 8px;
    > * {
        margin-right: 0.3rem;
    }
`

const StyledContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: auto;
    margin-left: 16px;
    margin-right: 16px;
    gap: 16px;
    @media (max-width: 1024px) {
        .leTuEP {
            color: #a50827;
        }
    }
`

const VendorRow = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: end;
    gap: 8px;
`

const StyledErrorIcon = styled(Icon)`
    title="Error";
    marginTop: 0;
    color: #b30d2f;
    flex-shrink: 0;
    min-width: 0;
    @media (max-width: 1024px) {
        color: #a50827;
    }
`

export default OnboardSelectionBar
